import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { DatePicker } from '@/components/ui/date-picker';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useAccessToken } from '@/services/auth';
import {
  isStaleVersionError,
  type OnFarmImplantQc,
  type OnFarmImplantQcAudit,
  onFarmImplantQcOptions,
  type OnFarmImplantQcSourceType,
  OnFarmImplantRequestError,
  type SaveOnFarmImplantQcRequest,
  useSaveOnFarmImplantQc,
} from '@/services/on-farm-implant';
import { parseWallClock, toWallClockDate } from '@/utils';

type QcTabProps = {
  sessionId: string | null;
};

type FreshForm = {
  ehmCreationDate: Date | undefined;
  strawLot: string;
  incubatorNumber: string;
  gunWarmerTemp: string;
  slideWarmerTemp: string;
  heatStageTemp: string;
};

type FrozenForm = {
  ehmCreationDate: Date | undefined;
  strawLot: string;
  incubatorNumber: string;
  gunWarmerTemp: string;
  thawThermosTemp: string;
  strayVoltageReading: string;
};

type SaveSection = (input: SaveOnFarmImplantQcRequest) => Promise<void>;

const useConfirmEmbryoCopy = () => {
  const confirm = useConfirm();
  const { t } = useTranslation();

  return (filledFields: (string | false)[], save: () => void) => {
    const fields = filledFields.filter(Boolean);
    if (!fields.length) {
      save();
      return;
    }
    confirm({
      title: t('onFarmImplant.qc.copyTitle'),
      description: t('onFarmImplant.qc.copyBody', {
        fields: fields.join(', '),
      }),
      confirmLabel: t('onFarmImplant.qc.copyConfirm'),
      confirmVariant: 'warning',
      onConfirm: save,
    });
  };
};

const TabMessage = ({ icon, title }: { icon: string; title: string }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-1 py-24 text-gray-400">
    <i className={`${icon} mb-2 text-4xl`} />
    <p className="text-base font-medium">{title}</p>
  </div>
);

const sectionVersion = (
  qc: OnFarmImplantQc,
  section: { audit: { sessionQcId: string } | null } | null,
) => (section?.audit ? qc.versions[section.audit.sessionQcId] : undefined);

const useSectionSave = ({
  save,
  version,
  isDirty,
  onReload,
}: {
  save: SaveSection;
  version: string | undefined;
  isDirty: boolean;
  onReload: () => void;
}) => {
  const [problems, setProblems] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loadedVersion] = useState(version);
  const savedElsewhere = version !== loadedVersion && !isSaving;

  useEffect(() => {
    if (savedElsewhere && !isDirty) onReload();
  }, [savedElsewhere, isDirty, onReload]);

  const run = async (input: SaveOnFarmImplantQcRequest) => {
    setProblems([]);
    setIsSaving(true);
    try {
      await save(input);
      onReload();
    } catch (error) {
      if (isStaleVersionError(error)) {
        onReload();
      } else if (
        error instanceof OnFarmImplantRequestError &&
        error.problems.length
      ) {
        setProblems(error.problems);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return { problems, isSaving, run, savedElsewhere: savedElsewhere && isDirty };
};

const QcCard = ({
  icon,
  title,
  sources,
  audit,
  readOnly,
  canSave,
  isSaving,
  problems,
  savedElsewhere,
  onReload,
  onSave,
  children,
}: {
  icon: string;
  title: string;
  sources: OnFarmImplantQcSourceType[];
  audit: OnFarmImplantQcAudit | null | undefined;
  readOnly: boolean;
  canSave: boolean;
  isSaving: boolean;
  problems: string[];
  savedElsewhere: boolean;
  onReload: () => void;
  onSave: () => void;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();

  const card = (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <i className={`${icon} text-blue-600`} />
          {title}
        </CardTitle>
        <CardDescription>
          {t('onFarmImplant.qc.appliesTo', {
            sources: sources
              .map((source) => t(`onFarmImplant.qc.sources.${source}`))
              .join(', '),
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {savedElsewhere && (
          <div
            role="status"
            className="flex items-center justify-between gap-3 rounded-md bg-amber-50 p-3 text-xs text-amber-800"
          >
            <span>{t('onFarmImplant.qc.savedElsewhere')}</span>
            <Button size="xs" variant="outline" onClick={onReload}>
              <i className="pi pi-refresh" />
              {t('onFarmImplant.qc.reload')}
            </Button>
          </div>
        )}
        {problems.length > 0 && (
          <div
            role="alert"
            className="rounded-md bg-red-50 p-3 text-xs text-red-700"
          >
            <ul className="list-inside list-disc">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}
        <div
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          onContextMenu={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-3 border-t py-3">
        <span className="text-xs text-gray-500">
          {audit
            ? t('onFarmImplant.qc.lastSaved', {
                name: audit.updatedByName ?? t('onFarmImplant.qc.unknownUser'),
                date: parseISO(audit.updatedAt).toLocaleString('en-US', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }),
              })
            : t('onFarmImplant.qc.notSaved')}
        </span>
        {!readOnly && (
          <Button size="sm" onClick={onSave} disabled={!canSave || isSaving}>
            <i className={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} />
            {t('common.save')}
          </Button>
        )}
      </CardFooter>
    </Card>
  );

  if (!audit) return card;

  return (
    <ContextMenu>
      <ContextMenuTrigger>{card}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onClick={() => window.open(`/audit/${audit.sessionQcId}`, '_blank')}
        >
          <i className="pi pi-history" />
          {t('onFarmImplant.qc.viewHistory')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

const FreshQcSection = ({
  qc,
  onSave,
  onReload,
}: {
  qc: OnFarmImplantQc;
  onSave: SaveSection;
  onReload: () => void;
}) => {
  const { t } = useTranslation();
  const readOnly = !qc.isEditable;
  const values = qc.fresh?.values;
  const [loadedVersions] = useState(qc.versions);
  const confirmCopy = useConfirmEmbryoCopy();
  const {
    control,
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FreshForm>({
    defaultValues: {
      ehmCreationDate: parseWallClock(values?.ehmCreationDate) ?? undefined,
      strawLot: values?.strawLot ?? '',
      incubatorNumber: values?.incubatorNumber ?? '',
      gunWarmerTemp: values?.gunWarmerTemp?.toString() ?? '',
      slideWarmerTemp: values?.slideWarmerTemp?.toString() ?? '',
      heatStageTemp: values?.heatStageTemp ?? '',
    },
    disabled: readOnly,
  });
  const { problems, isSaving, run, savedElsewhere } = useSectionSave({
    save: onSave,
    version: sectionVersion(qc, qc.fresh),
    isDirty,
    onReload,
  });

  const save = handleSubmit((form) =>
    confirmCopy(
      [
        !!form.ehmCreationDate && t('onFarmImplant.qc.ehmDate'),
        !!form.strawLot.trim() && t('onFarmImplant.qc.strawLot'),
        !!form.incubatorNumber.trim() && t('onFarmImplant.qc.incubatorNumber'),
        !!form.gunWarmerTemp && t('onFarmImplant.qc.gunWarmerTemp'),
      ],
      () =>
        void run({
          fresh: {
            ehmCreationDate: form.ehmCreationDate
              ? toWallClockDate(form.ehmCreationDate)
              : null,
            strawLot: form.strawLot.trim() || null,
            incubatorNumber: form.incubatorNumber.trim() || null,
            gunWarmerTemp: form.gunWarmerTemp
              ? Number(form.gunWarmerTemp)
              : null,
            slideWarmerTemp: form.slideWarmerTemp
              ? Number(form.slideWarmerTemp)
              : null,
            heatStageTemp: form.heatStageTemp.trim() || null,
          },
          versions: loadedVersions,
        }),
    ),
  );

  return (
    <QcCard
      icon="pi pi-circle"
      title={t('onFarmImplant.qc.fresh.title')}
      sources={qc.freshSources}
      audit={qc.fresh?.audit}
      readOnly={readOnly}
      canSave={isDirty}
      isSaving={isSaving}
      problems={problems}
      savedElsewhere={savedElsewhere}
      onReload={onReload}
      onSave={() => void save()}
    >
      <Field>
        <FieldLabel htmlFor="qc-fresh-ehm-date">
          {t('onFarmImplant.qc.ehmDate')}
        </FieldLabel>
        <Controller
          name="ehmCreationDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              id="qc-fresh-ehm-date"
              value={field.value}
              onChange={field.onChange}
              className="h-9"
              disabled={field.disabled}
            />
          )}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-fresh-straw-lot">
          {t('onFarmImplant.qc.strawLot')}
        </FieldLabel>
        <Input
          id="qc-fresh-straw-lot"
          maxLength={50}
          {...register('strawLot')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-fresh-incubator">
          {t('onFarmImplant.qc.incubatorNumber')}
        </FieldLabel>
        <Input
          id="qc-fresh-incubator"
          maxLength={20}
          {...register('incubatorNumber')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-fresh-gun-warmer">
          {t('onFarmImplant.qc.gunWarmerTemp')}
        </FieldLabel>
        <Input
          id="qc-fresh-gun-warmer"
          type="number"
          inputMode="decimal"
          step="0.1"
          {...register('gunWarmerTemp')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-slide-warmer">
          {t('onFarmImplant.qc.fresh.slideWarmerTemp')}
        </FieldLabel>
        <Input
          id="qc-slide-warmer"
          type="number"
          inputMode="decimal"
          step="0.1"
          {...register('slideWarmerTemp')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-heat-stage">
          {t('onFarmImplant.qc.fresh.heatStageTemp')}
        </FieldLabel>
        <Input
          id="qc-heat-stage"
          placeholder={t('onFarmImplant.qc.fresh.heatStagePlaceholder')}
          {...register('heatStageTemp')}
        />
      </Field>
    </QcCard>
  );
};

const FrozenQcSection = ({
  qc,
  onSave,
  onReload,
}: {
  qc: OnFarmImplantQc;
  onSave: SaveSection;
  onReload: () => void;
}) => {
  const { t } = useTranslation();
  const readOnly = !qc.isEditable;
  const values = qc.frozen?.values;
  const [loadedVersions] = useState(qc.versions);
  const confirmCopy = useConfirmEmbryoCopy();
  const {
    control,
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FrozenForm>({
    defaultValues: {
      ehmCreationDate: parseWallClock(values?.ehmCreationDate) ?? undefined,
      strawLot: values?.strawLot ?? '',
      incubatorNumber: values?.incubatorNumber ?? '',
      gunWarmerTemp: values?.gunWarmerTemp?.toString() ?? '',
      thawThermosTemp: values?.thawThermosTemp?.toString() ?? '',
      strayVoltageReading: values?.strayVoltageReading?.toString() ?? '',
    },
    disabled: readOnly,
  });
  const { problems, isSaving, run, savedElsewhere } = useSectionSave({
    save: onSave,
    version: sectionVersion(qc, qc.frozen),
    isDirty,
    onReload,
  });

  const save = handleSubmit((form) =>
    confirmCopy(
      [
        !!form.ehmCreationDate && t('onFarmImplant.qc.ehmDate'),
        !!form.strawLot.trim() && t('onFarmImplant.qc.strawLot'),
        !!form.incubatorNumber.trim() && t('onFarmImplant.qc.incubatorNumber'),
        !!form.gunWarmerTemp && t('onFarmImplant.qc.gunWarmerTemp'),
        !!form.thawThermosTemp && t('onFarmImplant.qc.frozen.thawThermosTemp'),
      ],
      () =>
        void run({
          frozen: {
            ehmCreationDate: form.ehmCreationDate
              ? toWallClockDate(form.ehmCreationDate)
              : null,
            strawLot: form.strawLot.trim() || null,
            incubatorNumber: form.incubatorNumber.trim() || null,
            gunWarmerTemp: form.gunWarmerTemp
              ? Number(form.gunWarmerTemp)
              : null,
            thawThermosTemp: form.thawThermosTemp
              ? Number(form.thawThermosTemp)
              : null,
            strayVoltageReading: form.strayVoltageReading
              ? Number(form.strayVoltageReading)
              : null,
          },
          versions: loadedVersions,
        }),
    ),
  );

  return (
    <QcCard
      icon="pi pi-asterisk"
      title={t('onFarmImplant.qc.frozen.title')}
      sources={qc.frozenSources}
      audit={qc.frozen?.audit}
      readOnly={readOnly}
      canSave={isDirty}
      isSaving={isSaving}
      problems={problems}
      savedElsewhere={savedElsewhere}
      onReload={onReload}
      onSave={() => void save()}
    >
      <Field>
        <FieldLabel htmlFor="qc-frozen-ehm-date">
          {t('onFarmImplant.qc.ehmDate')}
        </FieldLabel>
        <Controller
          name="ehmCreationDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              id="qc-frozen-ehm-date"
              value={field.value}
              onChange={field.onChange}
              className="h-9"
              disabled={field.disabled}
            />
          )}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-frozen-straw-lot">
          {t('onFarmImplant.qc.strawLot')}
        </FieldLabel>
        <Input
          id="qc-frozen-straw-lot"
          maxLength={50}
          {...register('strawLot')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-frozen-incubator">
          {t('onFarmImplant.qc.incubatorNumber')}
        </FieldLabel>
        <Input
          id="qc-frozen-incubator"
          maxLength={20}
          {...register('incubatorNumber')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-frozen-gun-warmer">
          {t('onFarmImplant.qc.gunWarmerTemp')}
        </FieldLabel>
        <Input
          id="qc-frozen-gun-warmer"
          type="number"
          inputMode="decimal"
          step="0.1"
          {...register('gunWarmerTemp')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-thaw-thermos">
          {t('onFarmImplant.qc.frozen.thawThermosTemp')}
        </FieldLabel>
        <Input
          id="qc-thaw-thermos"
          type="number"
          inputMode="decimal"
          step="0.1"
          {...register('thawThermosTemp')}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="qc-stray-voltage">
          {t('onFarmImplant.qc.frozen.strayVoltageReading')}
        </FieldLabel>
        <Input
          id="qc-stray-voltage"
          type="number"
          inputMode="decimal"
          step="0.001"
          {...register('strayVoltageReading')}
        />
      </Field>
    </QcCard>
  );
};
export const QcTab = ({ sessionId }: QcTabProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const { mutateAsync: saveQc } = useSaveOnFarmImplantQc();
  const [freshLoads, setFreshLoads] = useState(0);
  const [frozenLoads, setFrozenLoads] = useState(0);
  const reloadFresh = useCallback(() => setFreshLoads((n) => n + 1), []);
  const reloadFrozen = useCallback(() => setFrozenLoads((n) => n + 1), []);

  const {
    data: qc,
    isLoading,
    isError,
  } = useQuery({
    ...onFarmImplantQcOptions(token, { sessionId: sessionId ?? '' }),
    enabled: !!token && !!sessionId,
  });

  if (!sessionId) {
    return (
      <TabMessage
        icon="pi pi-check-circle"
        title={t('onFarmImplant.qc.noSession')}
      />
    );
  }

  if (isLoading) {
    return (
      <TabMessage icon="pi pi-spin pi-spinner" title={t('common.loading')} />
    );
  }

  if (isError || !qc) {
    return (
      <TabMessage
        icon="pi pi-exclamation-triangle"
        title={t('onFarmImplant.qc.loadError')}
      />
    );
  }

  const save =
    (successKey: string): SaveSection =>
    async (input) => {
      try {
        await saveQc({ sessionId, input });
        toast.add({ data: { variant: 'success' }, title: t(successKey) });
      } catch (error) {
        if (isStaleVersionError(error)) {
          toast.add({
            data: { variant: 'warning' },
            title: t('onFarmImplant.qc.stale'),
          });
        } else if (
          !(error instanceof OnFarmImplantRequestError) ||
          !error.problems.length
        ) {
          console.error('Failed to save QC:', error);
          toast.add({
            data: { variant: 'error' },
            title: t('onFarmImplant.qc.saveFailed'),
          });
        }
        throw error;
      }
    };

  const hasFresh = qc.freshSources.length > 0;
  const hasFrozen = qc.frozenSources.length > 0;

  return (
    <div className="flex flex-col gap-4 p-4">
      {!hasFresh && !hasFrozen ? (
        <TabMessage
          icon="pi pi-inbox"
          title={t('onFarmImplant.qc.noSections')}
        />
      ) : (
        <>
          {!qc.isEditable && (
            <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-800">
              {t('onFarmImplant.qc.readOnly')}
            </div>
          )}
          {hasFresh && (
            <FreshQcSection
              key={`fresh-${sessionId}-${qc.isEditable}-${freshLoads}`}
              qc={qc}
              onSave={save('onFarmImplant.qc.fresh.saved')}
              onReload={reloadFresh}
            />
          )}
          {hasFrozen && (
            <FrozenQcSection
              key={`frozen-${sessionId}-${qc.isEditable}-${frozenLoads}`}
              qc={qc}
              onSave={save('onFarmImplant.qc.frozen.saved')}
              onReload={reloadFrozen}
            />
          )}
        </>
      )}
    </div>
  );
};
